import React, { FormEvent, KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import userStyles from './AdminUsers.module.scss';
import styles from './AdminVideoList.module.scss';
import { AdminVideoListItemDto, PagedResult } from '../../../types/admin.types';
import { fetchAdminVideosPaged } from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { Routes } from '../../../types/constants';
import { parseSearchInput } from '../../../utils/video-search';
import {
    AdminFloatingVideoPreview,
    AdminVideoActionsMenu,
    AdminVideoPreviewTarget
} from './AdminVideoPlayerTools';

const pageSize = 25;
const emptyList: PagedResult<AdminVideoListItemDto> = {
    Results: [],
    PageIndex: 1,
    PageSize: pageSize,
    TotalPages: 0,
    TotalCount: 0
};

const publishLabels: Record<number, string> = { 0: 'Not published', 1: 'Published' };

const formatDate = (value?: string | null) => {
    if (!value) return 'No date';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
};

const mediaWorkflowPresentation = (video: AdminVideoListItemDto, canPlay: boolean) => {
    switch (video.MediaWorkflowStatus) {
        case 'NoSource':
            return {
                label: 'No source',
                className: userStyles.statusDanger,
                hint: canPlay ? 'Preview available; source missing' : 'Open to add an MP4 source'
            };
        case 'AwaitingUpload':
            return { label: 'Awaiting upload', className: userStyles.statusWarning, hint: 'Open to resume the source upload' };
        case 'Processing':
            return { label: 'Processing', className: userStyles.statusWarning, hint: 'Playback is being prepared' };
        case 'Ready':
            return {
                label: 'Ready',
                className: userStyles.statusSuccess,
                hint: canPlay ? 'Preview available' : 'Open to verify playback'
            };
        case 'NeedsAttention':
            return {
                label: 'Needs attention',
                className: userStyles.statusDanger,
                hint: canPlay ? 'Preview available; review media' : 'Open to review media'
            };
        default:
            break;
    }

    switch (video.PrimaryMediaHealth) {
        case 'Ready': return { label: 'Ready', className: userStyles.statusSuccess, hint: canPlay ? 'Preview available' : 'Open to verify playback' };
        case 'Processing': return { label: 'Processing', className: userStyles.statusWarning, hint: 'Playback is being prepared' };
        case 'NeedsAttention': return { label: 'Needs attention', className: userStyles.statusDanger, hint: canPlay ? 'Preview available; review media' : 'Open to review media' };
        case 'Missing': return { label: 'Missing', className: userStyles.statusNeutral, hint: 'Open to review media' };
        default: return { label: canPlay ? 'Preview enabled' : 'Not checked', className: userStyles.statusNeutral, hint: canPlay ? 'Preview available' : 'Open to review media' };
    }
};

const AdminVideoList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { getUserInfo } = useUserState();
    const token = getUserInfo()?.token;
    const [listData, setListData] = useState<PagedResult<AdminVideoListItemDto>>(emptyList);
    const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '');
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [requestVersion, setRequestVersion] = useState(0);
    const [previewVideo, setPreviewVideo] = useState<AdminVideoPreviewTarget | null>(null);
    const pageIndex = Math.max(1, Number(searchParams.get('page')) || 1);
    const query = searchParams.get('q') ?? '';
    const tags = useMemo(() => searchParams.getAll('tag').filter(Boolean), [searchParams]);

    useEffect(() => {
        setSearchInput([query, ...tags.map((tag) => `#${tag}`)].filter(Boolean).join(' '));
    }, [query, tags]);

    useEffect(() => {
        let active = true;
        setIsLoading(true);
        setErrorMessage(null);
        fetchAdminVideosPaged(pageIndex, pageSize, query, tags, token)
            .then((response) => {
                if (!active) return;
                setListData(response);
                if (response.TotalPages > 0 && pageIndex > response.TotalPages) {
                    const next = new URLSearchParams(searchParams);
                    next.set('page', String(response.TotalPages));
                    setSearchParams(next, { replace: true });
                }
            })
            .catch(() => {
                if (active) {
                    setErrorMessage('Unable to load videos.');
                    setListData(emptyList);
                }
            })
            .finally(() => {
                if (active) setIsLoading(false);
            });
        return () => { active = false; };
    }, [pageIndex, query, requestVersion, searchParams, setSearchParams, tags, token]);

    const updatePage = (page: number) => {
        const next = new URLSearchParams(searchParams);
        if (page <= 1) next.delete('page');
        else next.set('page', String(page));
        setSearchParams(next);
    };

    const applySearch = (event: FormEvent) => {
        event.preventDefault();
        const parsed = parseSearchInput(searchInput);
        const next = new URLSearchParams();
        if (parsed.query) next.set('q', parsed.query);
        parsed.tags.forEach((tag) => next.append('tag', tag));
        setSearchParams(next);
    };

    const returnTo = `${location.pathname}${location.search}`;
    const openVideo = useCallback((id: string) => {
        navigate(`${Routes.ADMIN_VIDEOS}/${id}`, { state: { videosReturnTo: returnTo } });
    }, [navigate, returnTo]);

    const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, id: string) => {
        if ((event.key === 'Enter' || event.key === ' ') && event.target === event.currentTarget) {
            event.preventDefault();
            openVideo(id);
        }
    };

    return (
        <div className={userStyles.page}>
            <header className={userStyles.pageHeader}>
                <div>
                    <div className={userStyles.eyebrow}>Content administration</div>
                    <h1>Videos</h1>
                    <p>Find a video, check whether it is ready for viewers, and follow the recommended next step.</p>
                </div>
                <button className={userStyles.primaryButton} type="button" onClick={() => navigate(Routes.ADMIN_VIDEO_NEW)}>
                    Create new video
                </button>
            </header>

            <section className={userStyles.filterPanel} aria-label="Video filters">
                <form className={userStyles.searchRow} onSubmit={applySearch}>
                    <label className={userStyles.searchField}>
                        <span>Search videos</span>
                        <input
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            placeholder="Title, fighter, event, venue, or #tag"
                            maxLength={300}
                        />
                    </label>
                    <button className={userStyles.secondaryButton} type="submit">Search</button>
                </form>
                {(query || tags.length > 0) ? (
                    <div className={userStyles.activeFilters} aria-label="Active filters">
                        {query ? <span>Search: {query}</span> : null}
                        {tags.map((tag) => <span key={tag}>#{tag}</span>)}
                        <button className={userStyles.clearFilters} type="button" onClick={() => setSearchParams({})}>Clear search</button>
                    </div>
                ) : null}
            </section>

            <section className={userStyles.listPanel} aria-labelledby="video-results-heading">
                <div className={userStyles.sectionHeader}>
                    <div>
                        <h2 id="video-results-heading">Video directory</h2>
                        <p>{isLoading ? 'Checking videos…' : `${listData.TotalCount.toLocaleString()} ${listData.TotalCount === 1 ? 'video' : 'videos'}`}</p>
                    </div>
                    {errorMessage ? <button className={userStyles.textButton} type="button" onClick={() => setRequestVersion((value) => value + 1)}>Retry</button> : null}
                </div>

                {errorMessage ? <div className={userStyles.errorBanner} role="alert">{errorMessage}</div> : null}
                {isLoading ? <div className={userStyles.loadingState}>Loading videos…</div> : null}
                {!isLoading && !errorMessage && listData.Results.length === 0 ? (
                    <div className={userStyles.emptyState}>
                        <h3>No videos found</h3>
                        <p>Try a different title, fighter, event, venue, or tag.</p>
                    </div>
                ) : null}

                {!isLoading && listData.Results.length > 0 ? (
                    <>
                        <div className={userStyles.tableWrap}>
                            <table className={`${userStyles.userTable} ${styles.videoTable}`}>
                                <thead><tr><th>Video</th><th>Event and date</th><th>Publication</th><th>Media workflow</th><th><span className={userStyles.visuallyHidden}>Actions</span></th></tr></thead>
                                <tbody>
                                    {listData.Results.map((video) => {
                                        const canPlay = Boolean(video.Url?.trim()) && video.CanPreview === true;
                                        const mediaWorkflow = mediaWorkflowPresentation(video, canPlay);
                                        return (
                                            <tr
                                                key={video.Id}
                                                tabIndex={0}
                                                onClick={(event) => {
                                                    if (!(event.target as HTMLElement).closest('a, button')) openVideo(video.Id);
                                                }}
                                                onKeyDown={(event) => handleRowKeyDown(event, video.Id)}
                                                aria-label={`Edit ${video.Title}`}
                                            >
                                                <td><button className={styles.videoIdentity} type="button" onClick={() => openVideo(video.Id)}><span><strong>{video.Title}</strong><small>{video.Url || 'No shortcode'}</small></span></button></td>
                                                <td><strong>{video.EventName || 'No event'}</strong><br /><small>{formatDate(video.EffectiveEventDate)}</small></td>
                                                <td><span className={`${userStyles.statusBadge} ${video.PublishStatus === 1 ? userStyles.statusSuccess : userStyles.statusNeutral}`}>{publishLabels[video.PublishStatus] ?? 'Unknown'}</span></td>
                                                <td>
                                                    <div className={styles.healthStack}>
                                                        <span className={`${userStyles.statusBadge} ${mediaWorkflow.className}`}>{mediaWorkflow.label}</span>
                                                        <small className={styles.healthHint}>{mediaWorkflow.hint}</small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className={styles.rowActions}>
                                                        <AdminVideoActionsMenu
                                                            title={video.Title}
                                                            shortcode={video.Url}
                                                            canPreview={canPlay}
                                                            onPreview={() => setPreviewVideo({ title: video.Title, shortcode: video.Url })}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className={userStyles.mobileCards}>
                            {listData.Results.map((video) => {
                                const canPlay = Boolean(video.Url?.trim()) && video.CanPreview === true;
                                const mediaWorkflow = mediaWorkflowPresentation(video, canPlay);
                                return (
                                    <article key={video.Id} className={`${userStyles.mobileUserCard} ${styles.mobileVideoCard}`}>
                                        <button className={styles.mobileVideoCardMain} type="button" onClick={() => openVideo(video.Id)}>
                                            <span className={userStyles.mobileCardTop}><strong>{video.Title}</strong><small>{formatDate(video.EffectiveEventDate)}</small></span>
                                            <span className={styles.mobileMeta}><span>{video.EventName || 'No event'}</span><span>{video.Url || 'No shortcode'}</span></span>
                                            <span className={userStyles.mobileBadges}>
                                                <span className={`${userStyles.statusBadge} ${video.PublishStatus === 1 ? userStyles.statusSuccess : userStyles.statusNeutral}`}>{publishLabels[video.PublishStatus] ?? 'Unknown'}</span>
                                                <span className={`${userStyles.statusBadge} ${mediaWorkflow.className}`}>Media: {mediaWorkflow.label}</span>
                                            </span>
                                        </button>
                                        <AdminVideoActionsMenu
                                            title={video.Title}
                                            shortcode={video.Url}
                                            canPreview={canPlay}
                                            onPreview={() => setPreviewVideo({ title: video.Title, shortcode: video.Url })}
                                        />
                                    </article>
                                );
                            })}
                        </div>

                        <nav className={userStyles.pagination} aria-label="Video list pages">
                            <button type="button" disabled={listData.PageIndex <= 1} onClick={() => updatePage(Math.max(1, listData.PageIndex - 1))}>Previous</button>
                            <span>Page {listData.PageIndex} of {Math.max(1, listData.TotalPages)}</span>
                            <button type="button" disabled={listData.PageIndex >= listData.TotalPages} onClick={() => updatePage(listData.PageIndex + 1)}>Next</button>
                        </nav>
                    </>
                ) : null}
            </section>
            <AdminFloatingVideoPreview video={previewVideo} token={token} onClose={() => setPreviewVideo(null)} />
        </div>
    );
};

export default AdminVideoList;
